import {
  Sparkles,
  Bot,
  Brain,
  BookOpen,
  Globe,
  FolderGit2,
  Zap,
  LayoutTemplate,
  Mic,
  Database,
  Compass,
  Mail,
  CreditCard,
  PhoneForwarded,
  BellRing,
  ShoppingBag,
  Smartphone,
  KeyRound,
  Lock,
  ShieldCheck,
  ShieldAlert,
  Sliders,
  Clock,
  Star,
  PhoneCall,
} from 'lucide-react';

export type NodeCategory = 'all' | 'brain' | 'knowledge' | 'actions' | 'commerce' | 'security';

export interface NodePaletteItem {
  type: string;
  label: string;
  category: 'brain' | 'knowledge' | 'actions' | 'commerce' | 'security';
  icon: any;
  tag: string;
  tagColor: string;
  borderColor: string;
  bgGradient: string;
  desc: string;
}

export const PALETTE_CATEGORIES: Array<{ id: NodeCategory; label: string; iconName?: string }> = [
  { id: 'all', label: 'All' },
  { id: 'brain', label: '🧠 Brain' },
  { id: 'knowledge', label: '📚 Knowledge' },
  { id: 'actions', label: '⚡ Actions' },
  { id: 'commerce', label: '💼 Commerce' },
  { id: 'security', label: '🛡️ Security' },
];

export const MASTER_PALETTE_NODES: NodePaletteItem[] = [
  // ── 🧠 1. Brain & Memory ──
  {
    type: 'lunaCoreNode',
    label: 'Luna Core Hub',
    category: 'brain',
    icon: Sparkles,
    tag: 'MASTER BRAIN',
    tagColor: 'bg-cyan-500/20 text-cyan-200 border-cyan-400/40',
    borderColor: 'border-cyan-400/80 hover:border-cyan-300',
    bgGradient: 'from-cyan-950/80 via-slate-900/90 to-blue-950/80',
    desc: 'Central brain: Avatar face, Luna voice, & wiring hub.',
  },
  {
    type: 'instructionNode',
    label: 'Instruction / Persona',
    category: 'brain',
    icon: Bot,
    tag: 'PERSONA',
    tagColor: 'bg-cyan-500/20 text-cyan-200 border-cyan-400/30',
    borderColor: 'border-cyan-500/30 hover:border-cyan-400',
    bgGradient: 'from-cyan-950/40 to-slate-900/60',
    desc: 'Defines AI personality, voice greeting, and prompt role.',
  },
  {
    type: 'selfImproveNode',
    label: 'Luna Self-Improvement',
    category: 'brain',
    icon: Brain,
    tag: 'SELF-LEARNING',
    tagColor: 'bg-sky-500/25 text-sky-200 border-sky-400/40',
    borderColor: 'border-sky-500/40 hover:border-sky-300',
    bgGradient: 'from-sky-950/60 to-slate-900/60',
    desc: 'Conversation gap detection + owner-confirmed knowledge enrichment (2-step approval).',
  },
  {
    type: 'lunaMemoryNode',
    label: 'Customer Memory Vault',
    category: 'brain',
    icon: Brain,
    tag: 'EPISODIC MEMORY',
    tagColor: 'bg-purple-500/20 text-purple-200 border-purple-400/40',
    borderColor: 'border-purple-500/40 hover:border-purple-400',
    bgGradient: 'from-purple-950/60 via-slate-900/90 to-indigo-950/60',
    desc: 'Token-shielded episodic memory & customer cross-session recognition.',
  },

  // ── 📚 2. Knowledge & Data ──
  {
    type: 'knowledgeNode',
    label: 'Knowledge Vault',
    category: 'knowledge',
    icon: BookOpen,
    tag: 'RAG VAULT',
    tagColor: 'bg-emerald-500/20 text-emerald-200 border-emerald-400/30',
    borderColor: 'border-emerald-500/30 hover:border-emerald-400',
    bgGradient: 'from-emerald-950/40 to-slate-900/60',
    desc: 'Grounding documents, FAQ, catalog rules & pricing.',
  },
  {
    type: 'scraperNode',
    label: 'AI Web Scraper',
    category: 'knowledge',
    icon: Globe,
    tag: 'CRAWLER',
    tagColor: 'bg-indigo-500/20 text-indigo-200 border-indigo-400/30',
    borderColor: 'border-indigo-500/30 hover:border-indigo-400',
    bgGradient: 'from-indigo-950/40 to-slate-900/60',
    desc: 'Crawls website URLs with Luna AI & populates RAG.',
  },
  {
    type: 'mediaVaultNode',
    label: 'Media Vault',
    category: 'knowledge',
    icon: FolderGit2,
    tag: 'MULTIMODAL',
    tagColor: 'bg-sky-500/20 text-sky-200 border-sky-400/30',
    borderColor: 'border-sky-500/30 hover:border-sky-400',
    bgGradient: 'from-sky-950/40 to-slate-900/60',
    desc: 'Multimodal vision photos, PDF brochures & rich media.',
  },
  {
    type: 'databaseNode',
    label: 'Database & CRM Storage',
    category: 'knowledge',
    icon: Database,
    tag: 'STORAGE',
    tagColor: 'bg-teal-500/25 text-teal-200 border-teal-400/40',
    borderColor: 'border-teal-400/40 hover:border-teal-300',
    bgGradient: 'from-teal-950/50 to-slate-900/60',
    desc: 'Direct structured SQL/JSON record persistence.',
  },

  // ── ⚡ 3. Real-time Actions & Comms ──
  {
    type: 'whatsappInstanceNode',
    label: 'WhatsApp Instance',
    category: 'actions',
    icon: Smartphone,
    tag: 'LIVE CHANNEL',
    tagColor: 'bg-emerald-500/20 text-emerald-200 border-emerald-400/40',
    borderColor: 'border-emerald-500/40 hover:border-emerald-300',
    bgGradient: 'from-emerald-950/60 to-slate-900/60',
    desc: 'WhatsApp Business API & Live 2-Way Voice Call Gateway.',
  },
  {
    type: 'webWidgetNode',
    label: 'Website Co-Pilot',
    category: 'actions',
    icon: Globe,
    tag: 'LIVE CHANNEL',
    tagColor: 'bg-cyan-500/20 text-cyan-200 border-cyan-400/40',
    borderColor: 'border-cyan-500/40 hover:border-cyan-300',
    bgGradient: 'from-cyan-950/60 to-slate-900/60',
    desc: 'Embeddable Live Voice & Chat Co-Pilot widget for external websites with custom name, avatar, and whitelisted domains.',
  },
  {
    type: 'actionNode',
    label: 'Action / WhatsApp Alert',
    category: 'actions',
    icon: Zap,
    tag: 'ACTION',
    tagColor: 'bg-purple-500/20 text-purple-200 border-purple-400/30',
    borderColor: 'border-purple-500/30 hover:border-purple-400',
    bgGradient: 'from-purple-950/40 to-slate-900/60',
    desc: 'Owner WhatsApp lead alerts, CRM leads & dispatches.',
  },
  {
    type: 'sipCallNode',
    label: 'Enterprise SIP & PBX Phone',
    category: 'actions',
    icon: PhoneCall,
    tag: 'ENTERPRISE TELEPHONY',
    tagColor: 'bg-cyan-500/25 text-cyan-200 border-cyan-400/40',
    borderColor: 'border-cyan-400/40 hover:border-cyan-300',
    bgGradient: 'from-cyan-950/60 to-slate-900/60',
    desc: 'Grandstream UCM & Landline PBX Trunk with Secretary Transfer.',
  },
  {
    type: 'callTransferNode',
    label: 'Call Transfer & Escalation',
    category: 'actions',
    icon: PhoneForwarded,
    tag: 'ESCALATION',
    tagColor: 'bg-indigo-500/25 text-indigo-200 border-indigo-400/40',
    borderColor: 'border-indigo-400/40 hover:border-indigo-300',
    bgGradient: 'from-indigo-950/50 to-slate-900/60',
    desc: 'Live voice call forward to human staff with WhatsApp ping.',
  },
  {
    type: 'intentAlertNode',
    label: 'Smart Intent Alerts',
    category: 'actions',
    icon: BellRing,
    tag: 'VIP ALERTS',
    tagColor: 'bg-rose-500/25 text-rose-200 border-rose-400/40',
    borderColor: 'border-rose-500/40 hover:border-rose-300',
    bgGradient: 'from-rose-950/60 to-slate-900/60',
    desc: 'Silently alerts owner on customer interests & buying intent.',
  },
  {
    type: 'emailSmtpNode',
    label: 'Email & SMTP Dispatch',
    category: 'actions',
    icon: Mail,
    tag: 'SMTP',
    tagColor: 'bg-emerald-500/20 text-emerald-200 border-emerald-400/30',
    borderColor: 'border-emerald-400/40 hover:border-emerald-300',
    bgGradient: 'from-emerald-950/50 to-slate-900/60',
    desc: 'Automated receipt, onboarding & alert emails.',
  },
  {
    type: 'followUpNode',
    label: 'Smart Follow-Up & Recovery',
    category: 'actions',
    icon: Clock,
    tag: 'RE-ENGAGEMENT',
    tagColor: 'bg-purple-500/25 text-purple-200 border-purple-400/40',
    borderColor: 'border-purple-500/40 hover:border-purple-300',
    bgGradient: 'from-purple-950/60 to-slate-900/60',
    desc: 'Autonomous abandoned conversation recovery & smart follow-up reminders.',
  },

  // ── 💼 4. Commerce & Conversion ──
  {
    type: 'productCatalogNode',
    label: 'Product Catalog & Menu',
    category: 'commerce',
    icon: ShoppingBag,
    tag: '🛍️ CATALOG',
    tagColor: 'bg-emerald-500/20 text-emerald-200 border-emerald-400/40',
    borderColor: 'border-emerald-500/40 hover:border-emerald-300',
    bgGradient: 'from-emerald-950/60 to-slate-900/60',
    desc: 'Store products, dishes, prices, and stock with real-time Luna sync.',
  },
  {
    type: 'reviewBoosterNode',
    label: 'Google Review Booster',
    category: 'commerce',
    icon: Star,
    tag: 'REVIEWS & SEO',
    tagColor: 'bg-amber-500/25 text-amber-200 border-amber-400/40',
    borderColor: 'border-amber-500/40 hover:border-amber-300',
    bgGradient: 'from-amber-950/60 to-slate-900/60',
    desc: 'Autonomous Google Maps rating booster with negative feedback firewall.',
  },
  {
    type: 'paymentGatewayNode',
    label: 'Payment Gateway',
    category: 'commerce',
    icon: CreditCard,
    tag: 'COMMERCE',
    tagColor: 'bg-emerald-500/25 text-emerald-200 border-emerald-400/40',
    borderColor: 'border-emerald-400/40 hover:border-emerald-300',
    bgGradient: 'from-emerald-950/50 to-slate-900/60',
    desc: 'Bank account transfer & payment link dispatches.',
  },
  {
    type: 'formGeneratorNode',
    label: 'Form Generator (GenUI)',
    category: 'commerce',
    icon: LayoutTemplate,
    tag: 'GENUI',
    tagColor: 'bg-cyan-500/20 text-cyan-200 border-cyan-400/30',
    borderColor: 'border-cyan-400/40 hover:border-cyan-300',
    bgGradient: 'from-cyan-950/50 to-slate-900/60',
    desc: 'Generates dynamic frontend forms from rule docs.',
  },
  {
    type: 'voiceTypingNode',
    label: 'Voice-Typing Node',
    category: 'commerce',
    icon: Mic,
    tag: 'VOICE TYPING',
    tagColor: 'bg-purple-500/20 text-purple-200 border-purple-400/30',
    borderColor: 'border-purple-400/40 hover:border-purple-300',
    bgGradient: 'from-purple-950/50 to-slate-900/60',
    desc: 'Autonomous form typing via live voice function calls.',
  },

  // ── 🛡️ 5. Security, RBAC & Boundary ──
  {
    type: 'securityFilterNode',
    label: 'Security & Anti-Hallucination',
    category: 'security',
    icon: ShieldCheck,
    tag: 'SHIELD',
    tagColor: 'bg-amber-500/20 text-amber-200 border-amber-400/40',
    borderColor: 'border-amber-500/40 hover:border-amber-400',
    bgGradient: 'from-amber-950/50 via-slate-900/90 to-amber-950/40',
    desc: 'Forbidden topics, hallucination guard, and PII protection.',
  },
  {
    type: 'callGuardNode',
    label: 'Call Guard & Anti-Sabotage',
    category: 'security',
    icon: ShieldAlert,
    tag: 'CALL SHIELD',
    tagColor: 'bg-rose-500/20 text-rose-200 border-rose-400/40',
    borderColor: 'border-rose-500/40 hover:border-rose-400',
    bgGradient: 'from-rose-950/60 via-slate-900/90 to-orange-950/60',
    desc: 'Silence hangup, max call duration limits, and ghost call purge.',
  },
  {
    type: 'authGuardNode',
    label: 'Auth Guard (RBAC)',
    category: 'security',
    icon: Lock,
    tag: 'RBAC',
    tagColor: 'bg-rose-500/20 text-rose-200 border-rose-400/30',
    borderColor: 'border-rose-400/40 hover:border-rose-300',
    bgGradient: 'from-rose-950/50 to-slate-900/60',
    desc: 'Zero-trust verification boundary for confidential records.',
  },
  {
    type: 'authGatewayNode',
    label: 'Auth Gateway',
    category: 'security',
    icon: KeyRound,
    tag: 'GATEWAY',
    tagColor: 'bg-amber-500/20 text-amber-200 border-amber-400/30',
    borderColor: 'border-amber-400/40 hover:border-amber-300',
    bgGradient: 'from-amber-950/50 to-slate-900/60',
    desc: 'Triggers client portal login & registration modal.',
  },
  {
    type: 'permissionNode',
    label: 'Permission / RBAC Guard',
    category: 'security',
    icon: Sliders,
    tag: 'PERMISSIONS',
    tagColor: 'bg-amber-500/20 text-amber-200 border-amber-400/30',
    borderColor: 'border-amber-400/40 hover:border-amber-300',
    bgGradient: 'from-amber-950/50 to-slate-900/60',
    desc: 'Configures granular client access boundaries and feature locks.',
  },
  {
    type: 'sectionNode',
    label: 'Section & Route Node',
    category: 'security',
    icon: Compass,
    tag: 'ROUTE',
    tagColor: 'bg-indigo-500/20 text-indigo-200 border-indigo-400/30',
    borderColor: 'border-indigo-400/40 hover:border-indigo-300',
    bgGradient: 'from-indigo-950/50 to-slate-900/60',
    desc: 'Swaps sub-persona and knowledge based on URL route.',
  },
];
